import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { Heading, CardLine } from "../../pages/IndividualView";
import theme from "../../theme";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { H3 } from "../Heading";
import { Label } from "../Label";
import { Paragraph } from "../Paragraph";
import { TextArea } from "../TextArea";
import { Button } from "../Button";
import { Toggle } from "../Toggle";
import { Slider } from "../Slider";
import { Modal } from "../Modal";
import { ReviewStatusPill } from "../ReviewStatusPill";
import { useGetSICIndustries } from "../../data/useGetSICIndustries";
import { useGetSectors } from "../../data/useGetSectors";
import { useCreatePortalNatureOfBusiness } from "../../data/useCreatePortalNatureOfBusiness";
import { useEditPortalNatureOfBusiness } from "../../data/useEditPortalNatureOfBusiness";
import { useDeletePortalNatureOfBusiness } from "../../data/useDeletePortalNatureOfBusiness";
import { natureOfBusinessValidationSchema } from "../../utils/validationSchema";
import { showSuccess, showError } from "../../utils/error-handling";
import { NatureOfBusiness, Profile } from "../../types/Profile";

type PickerView = "industry" | "sector" | null;

const linkStyle: React.CSSProperties = {
  color: theme.colors.link,
  cursor: "pointer",
  fontWeight: 600,
  fontFamily: '"Inter", sans-serif',
  fontSize: 14,
};

type PickerListProps = {
  title: string;
  description: string;
  options: { id: number; label: string }[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  onDone: () => void;
};

const PickerList = ({
  title,
  description,
  options,
  selectedIds,
  onToggle,
  onDone,
}: PickerListProps) => (
  <Box p={3}>
    <H3 fontSize={20} mb={1}>
      {title}
    </H3>
    <Paragraph color="gray.60" mb={3}>
      {description}
    </Paragraph>

    <Box overflowY="auto" maxHeight="calc(100vh - 333px)">
      {options.map((option) => (
        <Flex
          key={option.id}
          justifyContent="space-between"
          alignItems="center"
          py={2}
          borderBottom="1px solid #F3F4F6"
        >
          <Paragraph my={0}>{option.label}</Paragraph>
          <Toggle
            isChecked={selectedIds.includes(option.id)}
            onChange={() => onToggle(option.id)}
          />
        </Flex>
      ))}
    </Box>

    <Flex justifyContent="flex-end" mt={3}>
      <Button type="button" onClick={onDone}>
        Done
      </Button>
    </Flex>
  </Box>
);

export const NatureOfBusinessSection = ({ profile }: { profile: Profile }) => {
  const records = profile?.nature_of_business ?? [];

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<NatureOfBusiness | null>(
    null,
  );
  const [pickerView, setPickerView] = useState<PickerView>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();

  const { data: industries = [] } = useGetSICIndustries("all", true);
  const { data: sectors = [] } = useGetSectors("all", true);
  const { mutateAsync: createNatureOfBusiness } =
    useCreatePortalNatureOfBusiness();
  const { mutateAsync: editNatureOfBusiness } = useEditPortalNatureOfBusiness();
  const { mutateAsync: deleteNatureOfBusiness } =
    useDeletePortalNatureOfBusiness();

  const industryOptions = useMemo(
    () =>
      industries.map((industry) => ({
        id: industry.id,
        label: industry.code
          ? `${industry.code} - ${industry.name}`
          : industry.name,
      })),
    [industries],
  );

  const sectorOptions = useMemo(
    () => sectors.map((sector) => ({ id: sector.id, label: sector.name })),
    [sectors],
  );

  const openAdd = () => {
    setEditingRecord(null);
    setPickerView(null);
    setIsEditOpen(true);
  };

  const openEdit = (record: NatureOfBusiness) => {
    setEditingRecord(record);
    setPickerView(null);
    setIsEditOpen(true);
  };

  const closeSlider = () => {
    setIsEditOpen(false);
    setPickerView(null);
    setIsDeleteModalOpen(false);
  };

  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: ["profile", profile?.id] });
  };

  const initialValues = {
    description: editingRecord?.description ?? "",
    industryIds: editingRecord?.industry?.map((industry) => industry.id) ?? [],
    sectorIds: editingRecord?.sector?.map((sector) => sector.id) ?? [],
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const payload = {
        profile: Number(profile?.id),
        description: values.description.trim(),
        industry: values.industryIds,
        sector: values.sectorIds,
      };

      if (editingRecord?.id) {
        await editNatureOfBusiness({ params: payload, id: editingRecord.id });
      } else {
        await createNatureOfBusiness(payload);
      }

      invalidateProfile();
      showSuccess(
        `Nature of business ${editingRecord ? "updated" : "added"} successfully`,
      );
      closeSlider();
    } catch (e) {
      showError(
        e,
        `Failed to ${editingRecord ? "update" : "add"} nature of business`,
      );
    }
  };

  const handleDelete = async () => {
    if (!editingRecord?.id || isDeleting) return;

    setIsDeleting(true);

    try {
      await deleteNatureOfBusiness({ id: editingRecord.id });
      invalidateProfile();
      showSuccess("Nature of business deleted successfully");
      closeSlider();
    } catch (e) {
      showError(e, "Failed to delete nature of business");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      mt={3}
      background={theme.colors.white}
      borderRadius={12}
      p={16}
      border={`1px solid ${theme.colors.divider}`}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Heading fontSize={14}>Nature of business</Heading>

        <span style={linkStyle} onClick={openAdd}>
          Add
        </span>
      </Flex>

      {records.length === 0 ? (
        <CardLine>No nature of business information found.</CardLine>
      ) : (
        <Box>
          {records.map((record, i) => (
            <Box
              key={record.id ?? i}
              p="10px 0"
              borderTop={i > 0 ? `1px solid ${theme.colors.divider}` : "none"}
            >
              <Flex justifyContent="space-between" alignItems="flex-start">
                <CardLine>{record.description || "-"}</CardLine>

                <span style={linkStyle} onClick={() => openEdit(record)}>
                  Edit
                </span>
              </Flex>

              {record.industry?.length > 0 && (
                <Box mt={2}>
                  <Heading fontSize={12} color={theme.colors.muted}>
                    Industry
                  </Heading>
                  <Flex gap="8" flexWrap="wrap" mt={1}>
                    {record.industry.map((industry) => (
                      <ReviewStatusPill
                        key={industry.id}
                        label={
                          industry.code
                            ? `${industry.code} - ${industry.name}`
                            : industry.name
                        }
                      />
                    ))}
                  </Flex>
                </Box>
              )}

              {record.sector?.length > 0 && (
                <Box mt={2}>
                  <Heading fontSize={12} color={theme.colors.muted}>
                    Sector
                  </Heading>
                  <Flex gap="8" flexWrap="wrap" mt={1}>
                    {record.sector.map((sector) => (
                      <ReviewStatusPill key={sector.id} label={sector.name} />
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Slider isOpen={isEditOpen} onRequestClose={closeSlider} width="600px">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={natureOfBusinessValidationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) =>
            pickerView === "industry" ? (
              <PickerList
                title="Choose Industry"
                description="Please select industry you provide your work in."
                options={industryOptions}
                selectedIds={formik.values.industryIds}
                onToggle={(id) =>
                  formik.setFieldValue(
                    "industryIds",
                    formik.values.industryIds.includes(id)
                      ? formik.values.industryIds.filter((i) => i !== id)
                      : [...formik.values.industryIds, id],
                  )
                }
                onDone={() => setPickerView(null)}
              />
            ) : pickerView === "sector" ? (
              <PickerList
                title="Choose Sector"
                description="Please select sector you provide your work in."
                options={sectorOptions}
                selectedIds={formik.values.sectorIds}
                onToggle={(id) =>
                  formik.setFieldValue(
                    "sectorIds",
                    formik.values.sectorIds.includes(id)
                      ? formik.values.sectorIds.filter((i) => i !== id)
                      : [...formik.values.sectorIds, id],
                  )
                }
                onDone={() => setPickerView(null)}
              />
            ) : (
              <Box p={3}>
                <Flex justifyContent="space-between" alignItems="center" mb={3}>
                  <H3 fontSize={16}>
                    {editingRecord
                      ? "Edit Nature of Business"
                      : "Add Nature of Business"}
                  </H3>
                </Flex>

                <form onSubmit={formik.handleSubmit}>
                  <Box mb={3}>
                    <TextArea
                      label="Description"
                      name="description"
                      rows={4}
                      isRequired
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      hasError={Boolean(
                        formik.touched.description && formik.errors.description,
                      )}
                      errorMessage={
                        (formik.touched.description &&
                          (formik.errors.description as string)) ||
                        ""
                      }
                    />
                  </Box>

                  <Box mb={3}>
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Label>Industry</Label>
                      <span
                        style={linkStyle}
                        onClick={() => setPickerView("industry")}
                      >
                        Choose Industry
                      </span>
                    </Flex>

                    <Flex gap="8" flexWrap="wrap">
                      {formik.values.industryIds.length === 0 ? (
                        <Paragraph color="gray.60" fontSize={1} my={0}>
                          No industry selected.
                        </Paragraph>
                      ) : (
                        formik.values.industryIds.map((id) => {
                          const industry = industryOptions.find(
                            (option) => option.id === id,
                          );
                          return industry ? (
                            <ReviewStatusPill key={id} label={industry.label} />
                          ) : null;
                        })
                      )}
                    </Flex>
                  </Box>

                  <Box mb={4}>
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Label>Sector</Label>
                      <span
                        style={linkStyle}
                        onClick={() => setPickerView("sector")}
                      >
                        Choose Sector
                      </span>
                    </Flex>

                    <Flex gap="8" flexWrap="wrap">
                      {formik.values.sectorIds.length === 0 ? (
                        <Paragraph color="gray.60" fontSize={1} my={0}>
                          No sector selected.
                        </Paragraph>
                      ) : (
                        formik.values.sectorIds.map((id) => {
                          const sector = sectorOptions.find(
                            (option) => option.id === id,
                          );
                          return sector ? (
                            <ReviewStatusPill key={id} label={sector.label} />
                          ) : null;
                        })
                      )}
                    </Flex>
                  </Box>

                  <Flex justifyContent="space-between" alignItems="center">
                    {editingRecord ? (
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => setIsDeleteModalOpen(true)}
                        isDisabled={formik.isSubmitting}
                      >
                        Delete
                      </Button>
                    ) : (
                      <span />
                    )}

                    <Flex gap="12">
                      <Button
                        type="button"
                        variant="text"
                        onClick={closeSlider}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        isDisabled={
                          !formik.dirty ||
                          !formik.isValid ||
                          formik.isSubmitting
                        }
                      >
                        {formik.isSubmitting ? "Saving..." : "Save"}
                      </Button>
                    </Flex>
                  </Flex>
                </form>
              </Box>
            )
          }
        </Formik>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete nature of business?"
        content="This will permanently remove this nature of business record."
        onModalConfirmClick={handleDelete}
        secondaryButtonText="Cancel"
        primaryButtonText={isDeleting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={isDeleting}
      />
    </Box>
  );
};
